import { useState, useCallback, useMemo, useEffect } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Icon from "../controls/Icon";
import ChangeDomain from '../../routes/domain/modals/ChangeDomainModal';
import { useTranslation } from "react-i18next";
import UserSettingsModal from '../../routes/user/modals/UserSettingsModal';
import PubSub from '../../services/PubSub';
import AboutModal from '../AboutModal';
import ResetPasswordModal from '../../routes/user/modals/ResetPasswordModal';
import IconButton from '../controls/IconButton';
import { useAppContext } from '../../common/ApplicationServicesContext';
import { useNavigate } from 'react-router-dom';

const Controls = ({ fromVertical = false }) => {
    const [showChangeDomainModal, setShowChangeDomainModal] = useState(false);
    const [showUserSettingsModal, setShowUserSettingsModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [hasUnsavedChanges, setHasUnSavedChanges] = useState(0);
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const { domain, user, handleLogout } = useAppContext();

    useEffect(() => {
        const unsavedChangesSubId = PubSub.subscribe("has-unsaved-changes", () => {
            setHasUnSavedChanges((prev) => prev + 1);
        });
        const nounsavedChangesSubId = PubSub.subscribe("no-unsaved-changes", () => {
            setHasUnSavedChanges((prev) => prev === 0 ? 0 : prev - 1);
        });

        return () => {
            PubSub.unsubscribe("has-unsaved-changes", unsavedChangesSubId);
            PubSub.unsubscribe("no-unsaved-changes", nounsavedChangesSubId);
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "d") {
                e.preventDefault();
                if (!user.appAdmin && user.singleDomainUser) {
                    PubSub.publish("show_info", "OTM_CANT_CHANGE_DOMAIN", "OTM_WARNING");
                }
                else if (hasUnsavedChanges !== 0) {
                    PubSub.publish("show_info", "OTM_DOMAIN_UNSAVED_CHANGES_WARNING", "OTM_WARNING");
                }
                else {
                    setShowChangeDomainModal(prev => !prev);
                }
            }
        }

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [hasUnsavedChanges, user]);

    const handleChangeDomainModalClick = useCallback(() => {
        if (hasUnsavedChanges === 0) {
            setShowChangeDomainModal(true);
        } else {
            PubSub.publish("show_info", "OTM_DOMAIN_UNSAVED_CHANGES_WARNING", "OTM_WARNING");
        }
    }, [hasUnsavedChanges]);

    const getUserDisplayName = (user) => {
        if (user.firstName && user.lastName) {
            return user.firstName + " " + user.lastName;
        }

        return user.email;
    }

    const getDropdownMenu = useMemo(() => {
        if (user.appAdmin || !user.singleDomainUser) {
            return <Dropdown.Item onClick={handleChangeDomainModalClick}>{translate("OTM_SWITCH_DOMAINS")}</Dropdown.Item>
        }
        return <></>
    }, [user, handleChangeDomainModalClick, translate]);

    const renderButtons = useMemo(() => <>
        <IconButton
            name="Language"
            buttonClassName="ms-2 nav-link bg-transparent"
            onClick={() => window.open(process.env.REACT_APP_OWN_WEBSITE)}
            iconProps={{ style: { color: "inherit !important" } }}
        />

        <IconButton
            name="Help"
            buttonClassName="ms-2 nav-link bg-transparent"
            onClick={() => window.open(domain.helpUrl || process.env.REACT_APP_DEFAULT_HELP_URL)}
            iconProps={{ style: { color: "inherit !important" } }}
        />

        <Dropdown>
            <Dropdown.Toggle
                bsPrefix="nav-link"
                className={`border-0 ms-2 p-0 bg-transparent`}
            >
                <Icon icon="AccountCircle" className="d-block" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Header>{getUserDisplayName(user)}</Dropdown.Header>
                <Dropdown.Divider />
                {getDropdownMenu}

                <Dropdown.Item onClick={() => setShowUserSettingsModal(true)}>{translate("OTM_MY_SETTINGS")}</Dropdown.Item>
                <Dropdown.Item
                    onClick={() => {
                        setShowPasswordResetModal(true);
                    }}
                >
                    {translate("OTM_RESET_PASSWORD")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowAboutModal(true)}>{translate("OTM_ABOUT")}</Dropdown.Item>
                <Dropdown.Item onClick={() => handleLogout(navigate)}>{translate("OTM_LOGOUT")}</Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    </>, [domain, getDropdownMenu, user, handleLogout, translate, navigate])

    return (
        <>
            {fromVertical ?
                <div className="d-flex w-100 flex-row justify-content-end">
                    {renderButtons}
                </div>
                : <div className="collapse navbar-collapse">
                    <div className={`navbar-text mx-auto h4 p-0 ${process.env.REACT_APP_STAGE_LABEL ? 'text-info' : 'text-white'}`}>
                        {`${translate("OTM_CONFIGURE")} ${process.env.REACT_APP_STAGE_LABEL ? process.env.REACT_APP_STAGE_LABEL + ' ': ''}- ${domain.name}`}
                    </div>
                    <div className="navbar-nav">
                        {renderButtons}
                    </div>
                </div>
            }
            <ChangeDomain show={showChangeDomainModal} cancelText="OTM_CANCEL" onCancel={() => setShowChangeDomainModal(false)} />
            <UserSettingsModal show={showUserSettingsModal} onCancel={() => setShowUserSettingsModal(false)} id={user._id} />
            <ResetPasswordModal
                show={showPasswordResetModal}
                onConfirm={() => {
                    setShowPasswordResetModal(false);
                }}
                onCancel={() => {
                    setShowPasswordResetModal(false);
                }}
            />
            <AboutModal show={showAboutModal} onCancel={() => setShowAboutModal(false)} />
        </>
    );
};

export default Controls;
